import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Save, Eye, Star, Shield, Heart, Sparkles, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';

const WEEK_ICONS = [Shield, Star, Heart, Sparkles, Trophy, Star, Heart, Shield];
const WEEK_COLORS = [
  'from-blue-700 to-blue-900', 'from-indigo-700 to-indigo-900',
  'from-emerald-700 to-emerald-900', 'from-teal-700 to-teal-900',
  'from-cyan-700 to-cyan-900', 'from-violet-700 to-violet-900',
  'from-amber-700 to-amber-900', 'from-rose-700 to-rose-900',
];

function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewWeek, setPreviewWeek] = useState(null);

  useEffect(() => { fetchBanners(); }, []);

  const fetchBanners = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/weekly-banners`);
      if (res.ok) setBanners(await res.json());
    } catch (e) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (week) => {
    if (expandedWeek === week) {
      setExpandedWeek(null);
    } else {
      setExpandedWeek(week);
      const banner = banners.find(b => b.week === week);
      if (banner) {
        setEditData({
          title: banner.title,
          message: banner.message,
          tsf_message: banner.tsf_message || '',
          week_label: banner.week_label,
        });
      }
    }
  };

  const handleSave = async (week) => {
    setSaving(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/weekly-banners/${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      if (res.ok) {
        toast.success(`Banner for ${editData.week_label} saved`);
        fetchBanners();
        setExpandedWeek(null);
      }
    } catch (e) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const getWeekBadgeColor = (week) => {
    if (week < 0) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (week === 0) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading weekly banners...</div>;

  return (
    <div className="space-y-4" data-testid="banner-manager">
      <div>
        <h3 className="text-base font-bold text-zinc-900">Weekly Banner Manager</h3>
        <p className="text-xs text-zinc-500">{banners.length} weeks configured. Click any week to edit its welcome banner.</p>
      </div>

      {/* Preview overlay */}
      {previewWeek !== null && (() => {
        const banner = banners.find(b => b.week === previewWeek);
        if (!banner) return null;
        const colorIdx = Math.abs(previewWeek + 2) % WEEK_COLORS.length;
        const IconComp = WEEK_ICONS[Math.abs(previewWeek + 2) % WEEK_ICONS.length];
        return (
          <div className="fixed inset-0 z-[100] flex flex-col" data-testid="banner-preview">
            <div className={`absolute inset-0 bg-gradient-to-br ${WEEK_COLORS[colorIdx]}`} />
            <button
              onClick={() => setPreviewWeek(null)}
              className="absolute top-4 right-4 z-10 text-white/60 hover:text-white p-2 rounded-full hover:bg-white/10"
              data-testid="banner-preview-close"
            >
              Close Preview
            </button>
            <div className="relative flex-1 flex flex-col items-center justify-center px-6 max-w-lg mx-auto w-full">
              <div className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 mb-6">
                <IconComp className="w-10 h-10 text-white" />
              </div>
              <p className="text-center text-white/60 text-xs font-bold uppercase tracking-[0.2em] mb-2">{banner.week_label}</p>
              <h1 className="text-center text-white text-3xl sm:text-4xl font-bold mb-4 leading-tight">{banner.title}</h1>
              <p className="text-center text-white/90 text-base sm:text-lg leading-relaxed max-w-sm mx-auto mb-6">{banner.message}</p>
              {banner.tsf_message && (
                <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-4 max-w-sm mx-auto backdrop-blur-sm">
                  <p className="text-center text-white/80 text-sm italic leading-relaxed">"{banner.tsf_message}"</p>
                </div>
              )}
            </div>
            <div className="relative pb-10 px-6 flex justify-center">
              <button
                onClick={() => setPreviewWeek(null)}
                className="px-8 py-3.5 rounded-full bg-white text-zinc-900 text-sm font-bold shadow-lg"
              >
                Continue to Dashboard
              </button>
            </div>
          </div>
        );
      })()}

      {/* Banner list */}
      <div className="space-y-1.5">
        {banners.map(b => (
          <div key={b.week} className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid={`banner-week-${b.week}`}>
            {/* Header row */}
            <button
              onClick={() => handleExpand(b.week)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded-md text-xs font-bold border ${getWeekBadgeColor(b.week)}`}>
                  {b.week_label}
                </span>
                <span className="text-sm font-semibold text-zinc-900">{b.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setPreviewWeek(b.week); }}
                  className="text-blue-500 hover:text-blue-700 p-1"
                  title="Preview banner"
                  data-testid={`preview-banner-${b.week}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
                {expandedWeek === b.week
                  ? <ChevronUp className="w-4 h-4 text-zinc-400" />
                  : <ChevronDown className="w-4 h-4 text-zinc-400" />
                }
              </div>
            </button>

            {/* Expanded edit form */}
            {expandedWeek === b.week && (
              <div className="border-t border-zinc-100 p-4 space-y-3 bg-zinc-50">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-zinc-600 block mb-1">Week Label</label>
                    <input
                      value={editData.week_label || ''}
                      onChange={e => setEditData(p => ({ ...p, week_label: e.target.value }))}
                      className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid={`edit-week-label-${b.week}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-600 block mb-1">Title</label>
                    <input
                      value={editData.title || ''}
                      onChange={e => setEditData(p => ({ ...p, title: e.target.value }))}
                      className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid={`edit-title-${b.week}`}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-600 block mb-1">Congratulations / Encouragement Message</label>
                  <textarea
                    value={editData.message || ''}
                    onChange={e => setEditData(p => ({ ...p, message: e.target.value }))}
                    rows={3}
                    className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                    data-testid={`edit-message-${b.week}`}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-600 block mb-1">TSF Message (quote)</label>
                  <textarea
                    value={editData.tsf_message || ''}
                    onChange={e => setEditData(p => ({ ...p, tsf_message: e.target.value }))}
                    rows={2}
                    className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                    data-testid={`edit-tsf-${b.week}`}
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={() => handleSave(b.week)}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
                    data-testid={`save-banner-${b.week}`}
                  >
                    <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onClick={() => setPreviewWeek(b.week)}
                    variant="outline"
                    className="text-xs h-8"
                  >
                    <Eye className="w-3 h-3 mr-1" /> Preview
                  </Button>
                  <Button onClick={() => setExpandedWeek(null)} variant="outline" className="text-xs h-8">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BannerManager;
